import React, {
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import PropTypes from 'prop-types';
import { stringify } from 'qs';
import {
  Input,
  Select,
  Form,
  Switch,
  message,
  Spin,
} from 'antd';
import debounce from 'lodash.debounce';
import ModalWrapper from './ModalWrapper';
import { labelColors } from '../shared/colorSwatches';
import { captureAnalytics } from '../../actions/preload';

const stringifyOptions = {
  format: 'RFC1738',
  addQueryPrefix: true,
  sort: (a, b) => (a.localeCompare(b)),
};

// eslint-disable-next-line react/prop-types
const DebounceSelect = ({ fetchOptions, debounceTimeout = 800, ...props }) => {
  const [fetching, setFetching] = useState(false);
  const [options, setOptions] = useState([]);
  const fetchRef = useRef(0);
  const debounceFetcher = useMemo(() => {
    const loadOptions = (value) => {
      fetchRef.current += 1;
      const fetchId = fetchRef.current;
      setOptions([]);
      setFetching(true);
      fetchOptions(value).then((newOptions) => {
        if (fetchId !== fetchRef.current) {
          // for fetch callback order
          return;
        }
        setOptions(newOptions);
        setFetching(false);
      });
    };
    return debounce(loadOptions, debounceTimeout);
  }, [fetchOptions, debounceTimeout]);
  return (
    <Select
      labelInValue
      filterOption={false}
      onSearch={debounceFetcher}
      notFoundContent={fetching ? <Spin size="small" /> : null}
      /* eslint-disable-next-line react/jsx-props-no-spreading */
      {...props}
      options={options}
    />
  );
};

// team_users rows are positional [email, name, id] tuples
const userToOption = ([email, name, id]) => ({
  label: `${name} (${email})`,
  value: id,
});

// Resolve a team's saved color to its palette index; unknown/legacy colors
// fall back to the first swatch so the picker always has a valid selection.
const colorIndexFor = (color) => {
  const index = labelColors.findIndex((swatch) => swatch.color === color);
  return index === -1 ? 0 : index;
};

const AddTeam = (props) => {
  const {
    modalType,
    hideModal,
    createTeam,
    updateTeam,
    getTeamUsers,
    getOrganizationTeams,
    isLoading,
  } = props;
  const editedTeam = modalType.data?.team || null;
  const [teamName, setTeamName] = useState(editedTeam ? editedTeam.name : '');
  const [selectedColorIndex, setSelectedColorIndex] = useState(
    editedTeam ? colorIndexFor(editedTeam.color) : 0,
  );
  const [crossDepartmentVisibility, setCrossDepartmentVisibility] = useState(
    editedTeam ? !!editedTeam.cross_department_visibility : false,
  );
  const [selectedMembers, setSelectedMembers] = useState([]);
  // In edit mode we must not submit until the current members have loaded.
  // Otherwise a failed (or still-pending) prefetch would submit member_ids: []
  // and silently wipe every member off the team on a rename. Create mode has
  // nothing to prefetch, so it is ready immediately.
  const [membersReady, setMembersReady] = useState(!editedTeam);

  useEffect(() => {
    // Edit mode opens with the team's current members selected so they can be
    // removed inline from the multi-select.
    const loadCurrentMembers = async () => {
      if (!editedTeam) return;
      // The team serializer exposes the slug as `id` (see Team#object), so the
      // slug-keyed team_users lookup and update use editedTeam.id.
      // page_size=max loads EVERY current member: the endpoint's default page is
      // only 10, and submitting the prefetched member_ids replaces the whole
      // membership — a paged prefetch would silently drop members past page 1.
      const query = stringify({ team_slug: editedTeam.id, page_size: 'max' }, stringifyOptions);
      const response = await getTeamUsers(query);
      if (response && !response.error) {
        setSelectedMembers(response.data.users.map(userToOption));
        setMembersReady(true);
      } else {
        message.error({
          content: 'Could not load this team\'s members. Please reopen the editor before saving — saving now would remove everyone from the team.',
        });
      }
    };
    loadCurrentMembers();
  }, []);

  const fetchUserList = async (value) => {
    const query = stringify({ contains: value, page: 1 }, stringifyOptions);
    const response = await getTeamUsers(query);
    if (response && !response.error) {
      return response.data.users.map(userToOption);
    }
    return [];
  };

  const submit = async () => {
    if (!teamName || teamName.trim() === '') return;
    // Guard against submitting an empty member list before the edit-mode
    // prefetch has confirmed the team's current members (see membersReady).
    if (!membersReady) return;
    const selectedColor = labelColors[selectedColorIndex];
    const teamObject = {
      name: teamName.trim(),
      color: selectedColor.color,
      is_dark_text: selectedColor.darkText,
      cross_department_visibility: crossDepartmentVisibility,
      member_ids: selectedMembers.map((member) => member.value),
    };
    const result = editedTeam
      ? await updateTeam(editedTeam.id, teamObject)
      : await createTeam(teamObject);
    hideModal();
    if (result && result.error) {
      const fallback = editedTeam
        ? 'Error updating team. Please try again.'
        : 'Error adding team. Please try again.';
      message.error({ content: result.response?.data?.error || fallback });
    } else {
      captureAnalytics(editedTeam ? 'team_updated' : 'team_created', {
        has_cross_department_visibility: crossDepartmentVisibility,
        member_count: selectedMembers.length,
      });
      message.success({
        content: editedTeam ? 'Team successfully updated' : 'Team successfully added',
      });
    }
    getOrganizationTeams();
  };

  return (
    <ModalWrapper
      wrapClassName="settings-modal-wrap"
      hideModal={hideModal}
      customContentStyle={{ width: '420px' }}
      action={submit}
      actionName={editedTeam ? 'Save' : 'Add'}
      dismiss={isLoading ? '' : 'Cancel'}
      disabled={isLoading || !membersReady}
      form="addTeam"
      modalTitle={editedTeam ? 'Edit Team' : 'Add New Team'}
    >
      <Form
        layout="vertical"
        onFinish={submit}
        id="addTeam"
      >
        <Form.Item
          hasFeedback
          name="team"
          label="Team Name"
          rules={[
            {
              required: true,
              message: 'Must provide team name',
            },
          ]}
        >
          <Input
            value={teamName}
            defaultValue={teamName}
            onChange={(event) => setTeamName(event.target.value)}
          />
        </Form.Item>
        <Form.Item name="Color" label="Color">
          <div style={{ display: 'flex', flexWrap: 'wrap' }}>
            {labelColors.map((colorObject, index) => (
              <button
                type="button"
                aria-label={`Select ${colorObject.color}`}
                key={colorObject.color}
                onClick={() => setSelectedColorIndex(index)}
                style={{
                  height: 30,
                  width: 30,
                  borderRadius: 100,
                  borderStyle: 'inset',
                  border: index === selectedColorIndex ? '3px solid white' : '1px solid #d9d9d9',
                  backgroundColor: colorObject.color,
                  margin: '10px',
                  padding: 0,
                  boxShadow: index === selectedColorIndex ? '0px 0px 20px #b3b3b3' : '',
                  cursor: 'pointer',
                }}
              />
            ))}
          </div>
        </Form.Item>
        <Form.Item label="Members">
          <DebounceSelect
            mode="multiple"
            value={selectedMembers}
            placeholder="Search users by name or email"
            fetchOptions={fetchUserList}
            onChange={(newValue) => setSelectedMembers(newValue)}
            style={{ width: '100%' }}
          />
        </Form.Item>
        <Form.Item label="Cross-department visibility">
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <Switch
              checked={crossDepartmentVisibility}
              onChange={(checked) => setCrossDepartmentVisibility(checked)}
            />
            <span style={{ color: '#5c5c5c', fontSize: 13, lineHeight: 1.4 }}>
              Members can see documents tagged to this team even outside their
              departments.
            </span>
          </div>
        </Form.Item>
      </Form>
    </ModalWrapper>
  );
};

AddTeam.defaultProps = {
  isLoading: false,
};

AddTeam.propTypes = {
  hideModal: PropTypes.func.isRequired,
  modalType: PropTypes.object.isRequired,
  isLoading: PropTypes.bool,
  createTeam: PropTypes.func.isRequired,
  updateTeam: PropTypes.func.isRequired,
  getTeamUsers: PropTypes.func.isRequired,
  getOrganizationTeams: PropTypes.func.isRequired,
};

export default AddTeam;
